import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Share2, MessageCircle, Mail, Facebook, Link2, Phone, Check } from 'lucide-react';

export default function ShareButton({ eventName = '', variant = 'header' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const pageUrl = window.location.href;
  const shareText = t('ip.shareMessage', { event: eventName });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = pageUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const channels = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600',
      href: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + pageUrl)}`,
    },
    {
      id: 'sms',
      label: 'SMS',
      icon: Phone,
      color: 'bg-blue-500 hover:bg-blue-600',
      href: `sms:?body=${encodeURIComponent(shareText + ' ' + pageUrl)}`,
    },
    {
      id: 'email',
      label: 'Email',
      icon: Mail,
      color: 'bg-zinc-700 hover:bg-zinc-800',
      href: `mailto:?subject=${encodeURIComponent(eventName + ' — Brious\'O Folies')}&body=${encodeURIComponent(shareText + '\n\n' + pageUrl)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#0d65d9]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(shareText)}`,
    },
  ];

  const isFloating = variant === 'floating';

  return (
    <Popover>
      <PopoverTrigger asChild>
        {isFloating ? (
          <button
            className="fixed bottom-8 left-8 flex items-center gap-2 px-5 py-3 bg-fuchsia-600 text-white rounded-full shadow-lg hover:bg-fuchsia-700 hover:shadow-xl hover:scale-105 transition-all z-40 group"
            data-testid="share-btn-floating"
          >
            <Share2 className="h-5 w-5 transition-transform group-hover:rotate-12" />
            <span className="font-semibold text-sm tracking-wide">{t('ip.shareBtn')}</span>
          </button>
        ) : (
          <button
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-fuchsia-600 text-white rounded-full shadow-md hover:bg-fuchsia-700 hover:shadow-lg hover:scale-105 transition-all text-sm font-semibold tracking-wide group"
            data-testid="share-btn-header"
          >
            <Share2 className="h-4 w-4 transition-transform group-hover:rotate-12" />
            {t('ip.shareBtn')}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 rounded-2xl overflow-hidden shadow-2xl border-0"
        side={isFloating ? 'top' : 'bottom'}
        align={isFloating ? 'start' : 'center'}
        sideOffset={12}
      >
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 p-4 pb-3">
          <p className="text-white font-semibold text-sm">{t('ip.shareTitle')}</p>
          <p className="text-zinc-400 text-xs mt-0.5 truncate">{eventName}</p>
        </div>
        <div className="p-3 space-y-1.5 bg-white">
          {channels.map((ch) => (
            <a
              key={ch.id}
              href={ch.href}
              target={ch.id === 'sms' ? '_self' : '_blank'}
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-50 transition-colors group/item"
              data-testid={`share-${ch.id}`}
            >
              <div className={`p-2 rounded-lg ${ch.color} transition-all`}>
                <ch.icon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-medium text-zinc-700 group-hover/item:text-zinc-900">{ch.label}</span>
            </a>
          ))}
          <button
            onClick={handleCopy}
            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-50 transition-colors w-full text-left group/item"
            data-testid="share-copy-link"
          >
            <div className={`p-2 rounded-lg transition-all ${copied ? 'bg-emerald-500' : 'bg-fuchsia-500 hover:bg-fuchsia-600'}`}>
              {copied ? <Check className="h-4 w-4 text-white" /> : <Link2 className="h-4 w-4 text-white" />}
            </div>
            <span className="text-sm font-medium text-zinc-700 group-hover/item:text-zinc-900">
              {copied ? t('ip.shareCopied') : t('ip.shareCopyLink')}
            </span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
